import { useEffect, useRef, useState } from "react";

// Client data with logos and links
const clients = [
  {
    name: "Regency Group",
    url: "https://www.regencygroup.co.in/",
    // Using text as many logos require direct hosting
    color: "#1e40af",
  },
  {
    name: "Midtown Group",
    url: "https://midtowngroup.in/",
    color: "#7c3aed",
  },
  {
    name: "Maatr Group",
    url: "https://maatrgroup.com/",
    color: "#0891b2",
  },
  {
    name: "CaratLane",
    url: "https://www.caratlane.com/",
    color: "#be185d",
  },
  {
    name: "DMart",
    url: "https://www.dmart.in/",
    color: "#dc2626",
  },
  {
    name: "Vaishnavi Maternity",
    url: "https://www.vaishnavimaternity.com/",
    color: "#0d9488",
  },
  {
    name: "IGYM India",
    url: "https://www.instagram.com/igymindia/",
    color: "#ea580c",
  },
  {
    name: "Kumar Resort",
    url: "https://kumarresortlonavala.com/",
    color: "#16a34a",
  },
  {
    name: "Arihant Group",
    url: "https://www.asl.net.in/",
    color: "#2563eb",
  },
];

const LogoCard = ({ client }) => {
  return (
    <a
      href={client.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex-shrink-0 group"
      title={client.name}
    >
      <div 
        className="h-16 sm:h-18 lg:h-20 w-36 sm:w-44 lg:w-52 flex items-center justify-center px-4 py-3 bg-white rounded-xl border-2 border-slate-100 shadow-sm transition-all duration-300 group-hover:shadow-xl group-hover:border-brand-300 group-hover:scale-105 group-hover:-translate-y-1"
        style={{ 
          '--hover-color': client.color 
        }}
      >
        <span 
          className="text-sm sm:text-base font-bold text-center leading-tight transition-colors duration-300 group-hover:text-brand-600"
          style={{ color: client.color }}
        >
          {client.name}
        </span>
      </div>
    </a>
  );
};

export const ClienteleSection = () => {
  const scrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const animationRef = useRef(null);
  const positionRef = useRef(0);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const speed = 0.8;

    const animate = () => {
      if (!isPaused && container) {
        positionRef.current += speed;
        
        // Get the width of one set of logos
        const singleSetWidth = container.scrollWidth / 2;
        
        // Reset position when we've scrolled through one set
        if (positionRef.current >= singleSetWidth) {
          positionRef.current = 0;
        }
        
        container.style.transform = `translateX(-${positionRef.current}px)`;
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPaused]);

  // Duplicate clients for seamless infinite scroll
  const duplicatedClients = [...clients, ...clients];

  return (
    <section 
      id="clientele"
      className="py-16 lg:py-24 bg-gradient-to-b from-white via-slate-50/50 to-white" 
      data-testid="clientele-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          <span className="inline-block text-sm font-semibold text-accent-500 tracking-wider uppercase mb-3">
            Our Clients
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            Trusted By Leading Brands
          </h2>
          <div className="w-20 h-1.5 bg-gradient-to-r from-brand-500 to-accent-500 mx-auto rounded-full" />
        </div>

        {/* Logo Carousel Container */}
        <div 
          className="relative overflow-hidden py-6"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => setIsPaused(false)}
        >
          {/* Gradient Overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 lg:w-40 bg-gradient-to-r from-white via-white/90 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 lg:w-40 bg-gradient-to-l from-white via-white/90 to-transparent z-10 pointer-events-none" />
          
          {/* Scrolling Track */}
          <div 
            ref={scrollRef}
            className="flex gap-5 sm:gap-6 lg:gap-8 will-change-transform"
            style={{ width: 'max-content' }}
          >
            {duplicatedClients.map((client, index) => (
              <LogoCard key={`${client.name}-${index}`} client={client} />
            ))}
          </div>
        </div>

        {/* Pause indicator */}
        <div className="text-center mt-4 mb-6">
          <span className="text-xs text-slate-400 italic">
            {isPaused ? "⏸ Paused — move away to resume" : "Hover to pause"}
          </span>
        </div>

        {/* Credibility Text */}
        <p className="text-center text-slate-600 text-sm sm:text-base mt-6 max-w-3xl mx-auto leading-relaxed">
          We are proud to have delivered HVAC solutions across{" "}
          <span className="text-brand-600 font-medium">residential</span>,{" "}
          <span className="text-brand-600 font-medium">retail</span>,{" "}
          <span className="text-brand-600 font-medium">healthcare</span>,{" "}
          <span className="text-brand-600 font-medium">hospitality</span>, and{" "}
          <span className="text-brand-600 font-medium">commercial</span> developments.
        </p>
      </div>
    </section>
  );
};
